<template>
	<v-dialog v-model="dialog_open" max-width="75vw" overlay-opacity="0.9" persistent scrollable>
		<v-card>
			<v-card-title>
				<b>Bulk Import</b>
				<v-spacer/>
				<v-btn v-if="step > 1" fab depressed x-xmall color="white" @click="get_collections">
					<v-icon>fa fas fa-arrow-left</v-icon>
				</v-btn>
				<v-btn fab depressed x-small color="white" @click="$emit('dialog_close')">
					<v-icon>fa fas fa-x</v-icon>
				</v-btn>
			</v-card-title>

			<v-card-text>
				<div class="d-flex">
					<v-btn v-if="sources.goopennc.active" color="primary" depressed @click="get_collections">GoOpenNC</v-btn>
				</div>

				<v-divider v-if="step && display_data.length" class="my-3"/>

				<div v-if="processing.active" class="mt-4">
					<v-progress-linear
						v-model="progress"
						height="25"
      					color="amber"
      					:buffer-value="buffering"
						stream
					>
						<template v-slot:default="{ value }">
							<strong>{{ Math.ceil(value) }}% ({{ processing.current }}/{{ processing.total }})</strong>
						</template>
					</v-progress-linear>
				</div>

				<div v-if="step === 1 && display_data.length" class="step-container">
					<v-btn v-if="this.standards" color="tertiary" class="mb-4" small @click="view_standards_open = true">View Collected Standards</v-btn>
					<ul class="no-bullets">
						<li v-for="(item, index) in display_data" class="mb-3">
							<div class="d-flex">
								<v-btn class="mr-4" color="secondary" depressed small @click="get_resources(item)">Select</v-btn>

								{{ item.description }}

								<v-spacer/>
								<v-chip v-if="item.loaded" class="mr-4" color="primary" outlined small>Resources Loaded<span v-if="item.standards_aligned">&MediumSpace;& Standards Aligned</span></v-chip>
								<span v-if="item.resource_count"><b>{{ item.resource_count }} Resources</b></span>
							</div>
						</li>
					</ul>
				</div>

				<div v-if="step === 2 && display_data.length" class="step-container">
					<div class="d-flex mb-4">
						<v-btn v-if="unsaved_display_data_count && collection_standards_aligned" color="primary" small depressed @click="save_resource(display_data, true)">Save {{ unsaved_display_data_count }} Resources</v-btn>
						<v-btn v-else-if="unsaved_display_data_count" color="primary" class="ml-2" small depressed @click="bulk_align_standards">Align Standards</v-btn>
						<v-btn v-else color="tertiary" small depressed disabled>All {{ valid_display_data.length }} Valid Resources Saved</v-btn>
						<v-spacer/>
						<v-btn v-if="invalid_display_data.length" color="tertiary" small depressed @click="include_invalid = !include_invalid">
							{{ include_invalid ? 'Hide ' : 'Show '}}{{ invalid_display_data.length }} Invalid Links
						</v-btn>
					</div>

					<div class="d-flex mb-4 pt-1">
						<v-select v-model="selected.filters.subject" label="Subject" class="mr-2" :items="subject_filter_options" clearable hide-details dense outlined/>
						<v-select v-model="selected.filters.grade" label="Grade" class="mr-2" :items="grade_filter_options" clearable hide-details dense outlined/>
						<v-select v-model="selected.filters.source" label="Source" :items="source_filter_options" clearable hide-details dense outlined/>
					</div>

					<ul class="no-bullets">
						<li v-for="(item, index) in filtered_display_data" class="mb-3">
							<div class="d-flex">
								<v-btn class="mr-4" :color="(item.link || item.saved) ? 'tertiary' : 'secondary'" depressed small @click="view(item)">View</v-btn>

								{{ item.description }}

								<v-spacer/>


								<b v-if="item.saved">Saved</b>
								<b v-else-if="item.standards_aligned">Standards Aligned</b>
								<b v-if="item.link">Invalid Link</b>
							</div>
						</li>
					</ul>
				</div>
			</v-card-text>
		</v-card>

		<v-dialog v-model="view_open" max-width="70vw" overlay-opacity="0.9" persistent scrollable>
			<v-card>
				<v-card-title>
					<b>{{ selected.resource.description }}</b>

					<v-spacer/>

					<v-icon class="mr-2" small @click="open(selected.resource.url)">fa-solid fa-up-right-from-square</v-icon>

					<v-btn fab depressed x-small color="white" @click="view_open = false">
						<v-icon>fa fas fa-x</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text>
					<p v-for="(value, key, index) in selected.resource"><b>{{ key }}:</b> {{ value }}</p>
				</v-card-text>

				<v-card-actions v-if="!selected.resource.link && !selected.resource.saved">
					<v-spacer/>
					<v-btn v-if="selected.resource.standards_aligned" color="primary" @click="save_resource(selected.resource)">Save</v-btn>
					<v-btn v-else color="primary" @click="align_standards(selected.resource)">Align Standards</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="view_standards_open" max-width="70vw" overlay-opacity="0.9" persistent scrollable>
			<v-card>
				<v-card-title>
					<b>Collected Standards</b>

					<v-spacer/>

					<v-btn class="mr-4" color="primary" depressed small @click="check_block('Get Frameworks')">Get Frameworks</v-btn>

					<v-btn fab depressed x-small color="white" @click="view_standards_open = false">
						<v-icon>fa fas fa-x</v-icon>
					</v-btn>
				</v-card-title>

				<v-card-text v-if="standards">
					<div v-for="(data, standard) in standards">
						<b>{{ standard }}</b>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-dialog>
</template>

<script>
import { mapState } from 'vuex'

export default {
	data() {
		return {
			dialog_open: true,
			sources: {
				goopennc: {
					active: true,
					url: 'https://goopennc.oercommons.org/curated-collections',
					id: undefined,
					collections: []
				}
			},
			view_open: false,
			view_standards_open: false,
			display_data: [],
			step: 0,
			selected: {
				collection: {},
				resource: {},
				filters: {
					subject: undefined,
					grade: undefined,
					source: undefined
				}
			},
			cache: {},
			include_invalid: false,
			processing: {
				active: false,
				total: undefined,
				current: undefined,
				buffer: undefined
			},
			standards: undefined
		}
	},

	computed: {
		...mapState(['user_info', 'site_config',]),

		filtered_display_data() {
			let base = this.display_data
			if (!this.include_invalid) {
				base = this.valid_display_data
			}

			if (this.selected.filters.subject || this.selected.filters.grade || this.selected.filters.source) {
				let subject = this.selected.filters.subject
				let grade = this.selected.filters.grade
				let source = this.selected.filters.source
				return base.filter(x => {
					let include = true
					if (source) {
						include = x.provider && x.provider.trim() == source
					}
					if (include && grade) {

						include = x.grades && x.grades.includes(grade)
					}
					if (include && subject) {
						let subjects = x.subject?.map(x => x.trim())
						include = subjects && subjects.includes(subject)
					}

					return include
				})
			}
			
			return base
		},

		valid_display_data() {
			return this.display_data.filter(x => !x.hasOwnProperty('link'))
		},

		unsaved_display_data_count() {
			/** included to trigger udpate */
			let step = this.step
			return this.filtered_display_data.filter(x => !x.link && !x.saved).length
		},

		invalid_display_data() {
			return this.display_data.filter(x => x.hasOwnProperty('link'))
		},

		progress() {
			if (this.processing.active) {
				return Math.round((this.processing.current / this.processing.total) * 100)
			}
		},

		buffering() {
			if (this.processing.active) {
				return Math.round((this.processing.buffer / this.processing.total) * 100)
			}
		},

		collection_standards_aligned() {
			return !!this.selected.collection?.standards_aligned
		},

		subject_filter_options() {
			let output = []

			this.filtered_display_data.forEach(item => {
				if (item.subject) {
					item.subject.forEach(subj => {
						output.push(subj.trim())
					})
				}
			})

			output = [...new Set(output)].sort()

			return output
		},

		grade_filter_options() {
			let output = []

			this.filtered_display_data.forEach(item => {
				if (item.grades) {
					item.grades.forEach(grade => {
						output.push(grade.trim())
					})
				}
			})

			output = [...new Set(output)].sort((a, b) => {
				a = isNaN(a) ? 0 : a
				b = isNaN(a) ? 0 : b
				return a - b
			})

			return output
		},

		source_filter_options() {
			let output = []

			this.filtered_display_data.forEach(item => {
				if (item.provider) {
					output.push(item.provider.trim())
				}
			})

			output = [...new Set(output)].sort()

			return output
		}
	},

	watch: {
		'selected.filters': {
			handler(val){
				console.log(val)
			},
			deep: true
		}
	},

	created() {
		vapp.bulk_import_component = this
	},

	mounted() {
		this.initialize()
	},

	methods: {
		initialize() {
			console.clear()
		},

		get_collections() {
			this.get_saved_standards()
			if (this.sources.goopennc.collections.length){
				this.display_data = this.sources.goopennc.collections
				this.step = 1
			} else {
				let url = 'https://goopennc.oercommons.org/curated-collections'
				let id = this.generate_id('core')
				this.sources.goopennc.id = id
				this.step = 0

				U.ajax('retrieve_bulk_import_collection_data', {user_id: this.user_info.user_id, lp_unit_id: id}, result => {
					if ((result?.status ?? '') != 'ok') {
						this.$alert('Error in retrieve_bulk_import_collection_data')
					} else if (!empty(result.bulk_import_collection_data)) {
						this.sources.goopennc.collections = JSON.parse(result.bulk_import_collection_data)
						this.display_data = this.sources.goopennc.collections
						this.step++
					} else {
						let check_pages = []
						let collections = []
						let total = 320
						this.processing.total = total
						this.processing.current = 1
						this.processing.active = true

						for(let i = 1; i < total; i++) {
							let url_to_check = new URL('curated-collections/' + i, url).href
							
							check_pages.push(
								this.scrape_page(url_to_check)
									.then($html => { 
										if ($html) {
											let $title = $html.find('.curated-collection-title')

											if ($title.length) {
												let $counter = $html.find('.counter')
												collections.push({
													url: url_to_check,
													description: $title.text().trim(),
													id: this.generate_id(url_to_check),
													resource_count: parseInt($counter.text().trim()) ?? 0,
													resource_data: []
												})
											}
										}
									})
									.catch(err => {
										console.error(err)
									})
									.finally(() => {
										this.processing.current++
									})
								)
						}

						Promise.all(check_pages)
							.then(() => {
								this.save_scraped_data(collections, id)
									.then(() => {
										this.processing.active = false
										this.sources.goopennc.collections = collections
										this.display_data = collections
										this.step++
									})
							})
					}
				})
			}
		},

		async get_resources(collection) {
			this.selected.collection = collection

			/** get from file first */
			if (collection.resource_data.length) {
				this.display_data = collection.resource_data
				this.step = 2

				if (!collection.loaded) {
					this.mark_collection(collection, 'loaded')
				}

				return
			}

			U.loading_start()

			/** check saved file next */
			await U.ajax('retrieve_bulk_import_collection_data', {user_id: this.user_info.user_id, lp_unit_id: collection.id}, result => {
				if ((result?.status ?? '') != 'ok') {
					U.loading_stop()
					this.$alert('Error in retrieve_bulk_import_collection_data')
				} else if (!empty(result.bulk_import_collection_data)) {
					collection.resource_data = JSON.parse(result.bulk_import_collection_data)

					if (!collection.loaded) {
						this.mark_collection(collection, 'loaded')
					}

					this.display_data = collection.resource_data
					
					this.$nextTick(() => {
						U.loading_stop()
						this.step = 2
					})
				} else {
					U.loading_stop()

					/** set up progress display */
					this.step = 0
					this.processing.total = collection.resource_count
					this.processing.current = 0
					this.processing.buffer = 0
					this.processing.active = true

					/** get numbers */
					let batch_count = collection.resource_count / 20
					let full_batches = Math.floor(batch_count)
					let remainder = collection.resource_count % 20

					/** batch 1 - no ?batch_start= */
					let urls = [collection.url]

					/** full batches */
					if (full_batches > 1) {
						for(let i = 1; i < full_batches; i++) {
							let batch_start = 20 * i
							let batch_url = collection.url + '?batch_start=' + batch_start
							urls.push(batch_url)
						}
					}

					/** remainder */
					if (full_batches && remainder) {
						let remainder_url = collection.url + '?batch_start=' + (collection.resource_count - remainder)
						urls.push(remainder_url)
					}

					this.promise_each(urls, url => {
						return this.step_1(url)
					})
					.then(result => {
						let resources = []
						result.forEach(batch => {
							batch.forEach(resource => {
								resource.parent_id = collection.id
								if (resource) {
									resources.push(resource)
								}
							})
						})
						collection.resource_data = resources
						
						this.save_scraped_data(resources, collection.id)
							.then(() => {
								this.processing.active = false
								this.mark_collection(collection, 'loaded')
								this.display_data = resources
								this.step = 2
							})
							.catch(err => {
								console.warn(err)
							})
					})
				}
			})
		},

		async promise_each(arr, fn) { // take an array and a function
			let output = []
			for(const item of arr) await fn(item).then(result => { output.push(result) });
			return output
		},

		scrape_page(url) {
			return new Promise((resolve, reject) => {
				U.ajax('scrape_page', {user_id: this.user_info.user_id, url}, result => {
					if ((result?.status ?? '') != 'ok') {
						reject('Error in scrape_page: ' + url)
					} else {
						if (result.html) {
							resolve($(result.html))
						} else {
							reject('No html returned')
						}
					}
				})
			})
		},

		save_scraped_data(data, id) {
			let json = JSON.stringify(data)

			/** remove excess data from top level file */
			if (id === 3059615) {
				json = JSON.stringify(data.map(x => {
					let output = object_copy(x)
					output.resource_data = []
					return output
				}))
			}

			let payload = {
				user_id: this.user_info.user_id,
				lp_unit_id: id,
				bulk_import_collection_data: json
			}

			return new Promise((resolve, reject) => {
				U.ajax('save_bulk_import_collection_data', payload, result => {
					if ((result?.status ?? '') != 'ok') {
						reject('Error in save_bulk_import_collection_data')
					} else {
						resolve(result)
					}
				})
			})
		},

		async step_1(url) {
			let output
			await this.scrape_page(url)
				.then($html => {
					output = this.step_2($html, url)
				})
			return output
		},

		async step_2($html, url) {
			let $resources = $html.find('.js-index-item')

			if ($resources.length) {
				this.processing.buffer += $resources.length
				let resources = []

				$resources.each((i, el) => {
					let $resource = $(el)
					let $resource_link = $($resource.find('.js-item-link')[0])
					let description = $resource_link.text()
					url = new URL($resource_link.attr('href'), url).href
					let info = {}
					$resource.find('dt')
						.each((i, x) => {
							let $item = $(x)
							let key = $item.text().replace(/[^a-zA-Z0-9\s]/g, "")
							let value = $item.next().text()
							info[key] = value
						})

					resources.push({url, description, info})
				})

				let output
				await this.step_3(resources, url).then(result => { output = result })
				return output
			} else {
				return false
			}
		},

		async step_3(resources, url) {
			let output = []

			await this.promise_each(resources, async resource => {
				let resource_output
				await this.scrape_page(resource.url)
					.then(async $html => {
						await this.step_4($html, resource)
								.then(result => {
									resource_output = result
								})
					})
				return resource_output
			})
			.then(result => {
				output = result
			})

			return output
		},

		async step_4($html, resource) {
			let output = object_copy(resource)

			/** Get resource url */
			let $url = $html.find('.view-resource-link')
			if ($url) {
				output.url = $url.attr('href')
			}

			if (await this.valid_link(output.url)) {
				let info = object_copy(resource.info)
				info.extensions = {}
				delete output.info

				/** Get standards */
				let $standards = $html.find('.alignment-tag-link')
				if ($standards.length) {
					output.standards = []
					$standards.each((i, el) => {
						let $el = $(el)
						let standard = $el.text()
						output.standards.push({humanCodingScheme: standard, identifier: standard})
					})

				}

				/** Get long_description */
				let $long_description = $html.find('.materials-details-abstract').find('.text')
				if ($long_description) {
					output.long_description = $long_description.text()
				}

				/** Get info */
				$html.find('.materials-details-first-part').find('dt')
					.each((i, x) => {
						let $item = $(x)
						let key = $item.text().replace(/[^a-zA-Z0-9\s\/]/gm, "")
						let $value = $item.next()
						let value = $value.text().trim()

						switch(key) {
							case 'Material Type':
								let options = this.site_config.resource_content_type_options

								if ($value.children().length > 1) {
									value = null

									$value.children().not('.tx').each((i, y) => {
										let to_check = $(y).text().trim()
										let found = options.find(z => z == to_check)
										if (found) {
											value = found
										}
									})
								} else {
									value = options.find(z => z == value)
								}

								info[key] = value
								info.extensions.content_type = value
								break;
							default:
								info[key] = value
						}
					})

				/** Get additional info incl. License */
				$html.find('.material-details-second-part').find('dt')
					.each((i, x) => {
						let $item = $(x)
						let key = $item.text().replace(/[^a-zA-Z0-9\s\/]/gm, "")
						let $value = $item.next()
						let value = $value.text().trim()

						switch(key) {
							case 'License':
								let $license = $item.next().find('a')
								let text = $license.text().trim()
								let uri = $license.attr('href')
								if (text) {
									info.extensions.license_text = text
									info.extensions.license_uri = uri
								}
								break;
							default:
								info[key] = value
						}
					})
					
				/** Process info */
				/** type */
				output.type = 'website'
				
				/** grade */ 
				if (info.hasOwnProperty('Grades')) {
					output.grades = info['Grades'].match(/[0-9K]+/g)
				}

				/** subject */
				if (info.hasOwnProperty('Subject')) {
					output.subject = info['Subject'].split(',')
				}

				/** source */
				if (info.hasOwnProperty('Provider')) {
					info.extensions.content_source = info['Provider']
				}

				/** extensions */
				output.extensions = object_copy(info.extensions)

				/** everything else */
				Object.keys(info).forEach(key => {
					let lower_key = key.toLowerCase().replace(/\s/, '_')

					if (!output.hasOwnProperty(lower_key)) {
						output[lower_key] = info[key]
					}
				})

				/** mappings */
				output.mappings = []
				if (output.hasOwnProperty('subject') && output.subject) {
					if (Array.isArray(output.subject)) {
						output.subject.forEach(subject => {
							output.mappings.push(('subject-' + subject.toLowerCase().trim()))
						})
					} else {
						output.mappings.push(('subject-' + output.subject.toLowerCase()))
					}
				}

				if (output.hasOwnProperty('grades') && output.grades) {
					output.grades.forEach(grade => {
						output.mappings.push(('grade-' + grade))
					})
				}

				output.agency_sanctioned = 1

				this.processing.current++
				return output
			} else {
				output.link = 'invalid'
				this.processing.current++
				return output
			}
		},

		/** obsolete */
		// get_resource_batch($result, item, remainder = false) {
		// 	let clean_url = this.get_clean_url(item)
		// 	let $resources = $result.find('.js-index-item')

		// 	if ($resources.length) {
		// 		let output = []

		// 		if (remainder && $resources.length > remainder) {
		// 			$resources = $resources.slice($resources.length - remainder)
		// 		}

		// 		$resources.each((i, el) => {
		// 			let $resource = $(el)
		// 			let $resource_link = $($resource.find('.js-item-link')[0])
		// 			let description = $resource_link.text()
		// 			let url = new URL($resource_link.attr('href'), clean_url).href
		// 			let info = {}

		// 			$resource.find('dt')
		// 				.each((i, x) => {
		// 					let $item = $(x)
		// 					let key = $item.text().replace(/[^a-zA-Z0-9\s]/g, "")
		// 					let value = $item.next().text()
		// 					info[key] = value
		// 				})

		// 			output.push(
		// 				this.scrape_page(url)
		// 					// .then($html => {
		// 					// 	return this.get_resource_data($html, {description, info})
		// 					// })
		// 					// .catch(error => {
		// 					// 	console.error(error)
		// 					// })
		// 			)
		// 		})

		// 		this.promise_each(output, $html => {
		// 			return this.get_resource_data($html,{description, info})
		// 		})
		// 		.then(result => {
		// 			return result
		// 		})
		// 		.catch(err => {
		// 			console.error(err)
		// 		})
		// 		// return Promise.all(output)
		// 	} else {
		// 		return false
		// 	}
		// },

		// async get_resource_data($result, item) {
		// 	let item_to_process = object_copy(item)

		// 	/** Get resource url */
		// 	let $url = $result.find('.view-resource-link')
		// 	if ($url) {
		// 		item_to_process.url = $url.attr('href')
		// 	}

		// 	if(await this.valid_link(item_to_process.url)){
		// 		let info = object_copy(item.info)
		// 		info.extensions = {}
		// 		delete item_to_process.info

		// 		/** Get standards */
		// 		let $standards = $result.find('.alignment-tag-link')
		// 		if ($standards.length) {
		// 			item_to_process.standards = []
		// 			$standards.each((i, el) => {
		// 				let $el = $(el)
		// 				let standard = $el.text()
		// 				item_to_process.standards.push({humanCodingScheme: standard, identifier: standard})
		// 			})

		// 		}

		// 		/** Get long_description */
		// 		let $long_description = $result.find('.materials-details-abstract').find('.text')
		// 		if ($long_description) {
		// 			item_to_process.long_description = $long_description.text()
		// 		}

		// 		/** Get info */
		// 		$result.find('.materials-details-first-part').find('dt')
		// 			.each((i, x) => {
		// 				let $item = $(x)
		// 				let key = $item.text().replace(/[^a-zA-Z0-9\s\/]/gm, "")
		// 				let $value = $item.next()
		// 				let value = $value.text().trim()

		// 				switch(key) {
		// 					case 'Material Type':
		// 						let options = this.site_config.resource_content_type_options

		// 						if ($value.children().length > 1) {
		// 							value = null

		// 							$value.children().not('.tx').each((i, y) => {
		// 								let to_check = $(y).text().trim()
		// 								let found = options.find(z => z == to_check)
		// 								if (found) {
		// 									value = found
		// 								}
		// 							})
		// 						} else {
		// 							value = options.find(z => z == value)
		// 						}

		// 						info[key] = value
		// 						info.extensions.content_type = value
		// 						break;
		// 					default:
		// 						info[key] = value
		// 				}
		// 			})

		// 		/** Get additional info incl. License */
		// 		$result.find('.material-details-second-part').find('dt')
		// 			.each((i, x) => {
		// 				let $item = $(x)
		// 				let key = $item.text().replace(/[^a-zA-Z0-9\s\/]/gm, "")
		// 				let $value = $item.next()
		// 				let value = $value.text().trim()

		// 				switch(key) {
		// 					case 'License':
		// 						let $license = $item.next().find('a')
		// 						let text = $license.text().trim()
		// 						let uri = $license.attr('href')
		// 						if (text) {
		// 							info.extensions.license_text = text
		// 							info.extensions.license_uri = uri
		// 						}
		// 						break;
		// 					default:
		// 						info[key] = value
		// 				}
		// 			})
					
		// 		/** Process info */
		// 		/** type */
		// 		item_to_process.type = 'website'
				
		// 		/** grade */ 
		// 		if (info.hasOwnProperty('Grades')) {
		// 			item_to_process.grades = info['Grades'].match(/[0-9K]+/g)
		// 		}

		// 		/** subject */
		// 		if (info.hasOwnProperty('Subject')) {
		// 			item_to_process.subject = info['Subject'].split(',')
		// 		}

		// 		/** source */
		// 		if (info.hasOwnProperty('Provider')) {
		// 			info.extensions.content_source = info['Provider']
		// 		}

		// 		/** extensions */
		// 		item_to_process.extensions = object_copy(info.extensions)

		// 		/** everything else */
		// 		Object.keys(info).forEach(key => {
		// 			let lower_key = key.toLowerCase().replace(/\s/, '_')

		// 			if (!item_to_process.hasOwnProperty(lower_key)) {
		// 				item_to_process[lower_key] = info[key]
		// 			}
		// 		})

		// 		/** mappings */
		// 		item_to_process.mappings = []
		// 		if (item_to_process.hasOwnProperty('subject') && item_to_process.subject) {
		// 			if (Array.isArray(item_to_process.subject)) {
		// 				item_to_process.subject.forEach(subject => {
		// 					item_to_process.mappings.push(('subject-' + subject.toLowerCase().trim()))
		// 				})
		// 			} else {
		// 				item_to_process.mappings.push(('subject-' + item_to_process.subject.toLowerCase()))
		// 			}
		// 		}

		// 		if (item_to_process.hasOwnProperty('grades') && item_to_process.grades) {
		// 			item_to_process.grades.forEach(grade => {
		// 				item_to_process.mappings.push(('grade-' + grade))
		// 			})
		// 		}

		// 		item_to_process.agency_sanctioned = 1

		// 		this.processing.current++

		// 		return item_to_process
		// 	} else {
		// 		item_to_process.link = 'invalid'
		// 		this.processing.current++
		// 		return item_to_process
		// 	}
		// },

		save_resource(item, bulk = false) {
			if (bulk) {
				this.step = 0
				let items_to_process = []

				item.forEach(x => {
					if (!x.link && !x.saved) {
						x.saved = true
						let r = new Resource({
							resource_id: 'import',
							...x
						})

						items_to_process.push({ resource: r })
					}
				})

				let batches = []

				for (let i = 0; i < items_to_process.length; i += 20) {
					batches.push(items_to_process.slice(i, i + 20))
				}

				this.processing.total = items_to_process.length
				this.processing.current = 0
				this.processing.buffer = 0
				this.processing.active = true

				let batch_number = 1
				this.promise_each(batches, batch => {
					this.processing.buffer += 20
					let processing = []

					batch.forEach(resource => {
						processing.push(this.$store.dispatch('save_resource', resource).then(() => { this.processing.current++ }))
					})

					return Promise.all(processing).then(() => { 
						batch_number++
					})
				})
				.then(() => {
					this.processing.active = false
					this.step = 2
					this.save_scraped_data(this.selected.collection.resource_data, this.selected.collection.id)
				})
			} else {
				U.loading_start()

				/** create new resource */
				let r = new Resource({
					resource_id: 'import',
					...item
				})

				this.$store.dispatch('save_resource', { resource: r }).then(result => {
					this.step = 0
					/** mark item as saved */
					item.saved = true

					/** save back to file */
					/** when it is only one resource the collection is already updated */
					this.save_scraped_data(this.selected.collection.resource_data, this.selected.collection.id)
						.then(() => {
							this.step = 2
							U.loading_stop()
							this.view_open = false
						})
				})
			}
		},

		get_clean_url(item) {
			if (item.hasOwnProperty('url')) return item.url.trim()
			if (item.hasOwnProperty('href')) return item.href.trim()
			return item.trim()
		},

		valid_link(url) {
			return new Promise( resolve => {
					U.ajax('check_url', {user_id: this.user_info.user_id, url: url}, result => {
						resolve(result.valid)
					})
				}
			)
		},

		view(item) {
			this.selected.resource = item
			this.view_open = true
		},

		open(url) {
			window.open(url, "_blank")
		},

		generate_id(str) {
			let hash = 0;
			for (let i = 0; i < str.length; i++) {
				let charCode = str.charCodeAt(i)
				hash = (hash << 5) - hash + charCode
				hash |= 0
			}
			return Math.abs(hash)
		},

		mark_collection(collection, property) {
			collection[property] = true
			this.save_scraped_data(this.sources.goopennc.collections, this.sources.goopennc.id)
		},

		get_saved_standards() {
			return new Promise((resolve, reject) => {
				if (this.standards) {
					resolve(true)
				} else {
					let id = this.generate_id('hash')
					U.ajax('retrieve_bulk_import_collection_data', {user_id: this.user_info.user_id, lp_unit_id: id}, result => {
						if ((result?.status ?? '') != 'ok') {
							reject('Error in retrieve_bulk_import_collection_data')
						} else if (!empty(result.bulk_import_collection_data)) {
							this.standards = JSON.parse(result.bulk_import_collection_data)
							resolve(true)
						} else {
							resolve(false)
						}
					})
				}
			})
		},

		async bulk_align_standards() {
			U.loading_start()

			/** Loop through display data */
			let save_step = this.step * 1
			this.step = 0
			this.display_data.forEach(resource => {
				this.align_standards(resource)
			})


			await this.save_scraped_data(this.display_data, this.selected.collection.id)
				.then(() => {
					this.mark_collection(this.selected.collection, 'standards_aligned')
					this.step = save_step
					U.loading_stop()
				})
		},

		align_standards(resource) {
			let standards = this.standards
			
			if (resource.standards){
				resource.standards.forEach(standard => {
					/** currently only 'Math' will need other options */
					let code = standard.humanCodingScheme.match(/(.+).Math(.+)/)

					if (code) {
						let match_code = code[1] + code[2]
						
						if (standards.hasOwnProperty(match_code)) {
							let match_standard = standards[match_code]
							
							standard.identifier = match_standard.identifier
						} else {
							delete standard.identifier
						}
					} else {
						delete standard.identifier
					}
				})
				resource.standards_aligned = true
			}
		},

		/** start checkblock */
		check_block() {
			console.log(...arguments)
			U.loading_stop()
		},
		/** end checkblock */
	}
}
</script>

<style lang="scss" scoped>
.step-container {
	max-height: 50vh;
	overflow: scroll;
}

.v-btn {
  text-transform:none !important;
}

ul {
	&.no-bullets {
		list-style-type: none; /* Remove bullets */
		padding: 0; /* Remove padding */
		margin: 0; /* Remove margins */
	}
}

.info-group {
	margin-left: 80px;
}
</style>